import { Checkbox } from '@praetorian-chariot/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface SliderProps {
  min?: number;
  max?: number;
  value?: number;
  onChange?: (value: number) => void;
  enableUnlimited?: boolean;
  unlimitedLabel?: string;
  className?: string;
  label?: string;
  title?: string;
  subtitle?: string;
  unit?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onUnlimitedChange?: (isUnlimited: boolean) => void;
  'data-testid'?: string;
}

const sizeClasses = {
  sm: {
    track: 'h-[5px]',
    thumb: 'w-5 h-5',
    label: 'text-xs',
    value: 'text-xs',
    marker: 'text-xs',
  },
  md: {
    track: 'h-[5px]',
    thumb: 'w-4 h-4',
    label: 'text-xs',
    value: 'text-xs',
    marker: 'text-sm',
  },
  lg: {
    track: 'h-[5px]',
    thumb: 'w-4 h-4',
    label: 'text-base',
    value: 'text-base',
    marker: 'text-sm',
  },
};

export const Slider: React.FC<SliderProps> = ({
  min = 0,
  max = 100,
  value = 0,
  onChange,
  enableUnlimited = false,
  unlimitedLabel = 'Unlimited',
  className,
  label,
  title,
  subtitle,
  unit = '',
  disabled = false,
  size = 'md',
  onUnlimitedChange,
  'data-testid': dataTestId,
}) => {
  const UNLIMITED_VALUE = 999999; // Special value for unlimited
  const [isDragging, setIsDragging] = useState(false);
  const [isUnlimited, setIsUnlimited] = useState(value === UNLIMITED_VALUE);
  const [dragOffset, setDragOffset] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const classes = sizeClasses[size];

  // Sync isUnlimited state when value prop changes
  useEffect(() => {
    setIsUnlimited(value === UNLIMITED_VALUE);
  }, [value, UNLIMITED_VALUE]);

  // Handle unlimited checkbox toggle
  const handleUnlimitedToggle = useCallback(() => {
    const newUnlimitedState = !isUnlimited;
    setIsUnlimited(newUnlimitedState);

    if (newUnlimitedState) {
      // Going to unlimited
      onChange?.(UNLIMITED_VALUE);
    } else {
      // Going back to limited - set to max
      onChange?.(max);
    }

    onUnlimitedChange?.(newUnlimitedState);
  }, [isUnlimited, UNLIMITED_VALUE, max, onChange, onUnlimitedChange]);

  // Calculate positions - full track, no segments
  const normalRange = max - min;

  const getPositionFromValue = useCallback(
    (val: number) => {
      if (val === UNLIMITED_VALUE) {
        return 100; // Unlimited goes to the far right
      }

      const normalizedValue = Math.max(
        0,
        Math.min((val - min) / normalRange, 1)
      );
      return normalizedValue * 100;
    },
    [min, normalRange, UNLIMITED_VALUE]
  );

  const getValueFromPosition = useCallback(
    (percent: number) => {
      const normalizedPercent = Math.max(0, Math.min(percent / 100, 1));
      return Math.round(min + normalizedPercent * normalRange);
    },
    [min, normalRange]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || isUnlimited || !trackRef.current) return;

      const rect = trackRef.current.getBoundingClientRect();
      const thumbCenterX =
        rect.left + (getPositionFromValue(value) / 100) * rect.width;
      const clickOffset = e.clientX - thumbCenterX;

      setDragOffset(clickOffset);
      setIsDragging(true);
      e.preventDefault();
    },
    [disabled, isUnlimited, getPositionFromValue, value]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !trackRef.current) return;

      const rect = trackRef.current.getBoundingClientRect();
      const adjustedClientX = e.clientX - dragOffset;
      const percent = ((adjustedClientX - rect.left) / rect.width) * 100;
      const newValue = getValueFromPosition(percent);

      setIsUnlimited(false); // Moving slider manually disables unlimited
      onChange?.(newValue);
    },
    [isDragging, dragOffset, getValueFromPosition, onChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || isUnlimited || isDragging) return;

      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;

      const percent = ((e.clientX - rect.left) / rect.width) * 100;
      const newValue = getValueFromPosition(percent);

      setIsUnlimited(false); // Clicking track disables unlimited
      onChange?.(newValue);
    },
    [disabled, isUnlimited, isDragging, getValueFromPosition, onChange]
  );

  const thumbPosition = getPositionFromValue(value);
  const displayValue = isUnlimited ? '∞' : `${value}${unit ? ` ${unit}` : ''}`;

  // Smart label merging - check if thumb is close to max marker
  const maxMarkerPosition = 100;
  const isNearMax = Math.abs(thumbPosition - maxMarkerPosition) < 15; // Within 15% of each other
  const shouldMergeLabels = isNearMax && !isUnlimited;

  // Check if thumb is close to min marker
  const isNearMin = thumbPosition < 15; // Within 15% of start
  const shouldHideMin = isNearMin && !isUnlimited;

  return (
    <div
      className={twMerge('w-full', className)}
      ref={sliderRef}
      data-testid={dataTestId}
    >
      {(title || label) && (
        <div className="mb-4">
          {title && (
            <div className="flex items-center justify-between">
              <h3 className="text-md text-chariot-text-primary pb-1 font-bold">
                {title}
              </h3>
              {enableUnlimited && (
                <div
                  className="flex cursor-pointer items-center gap-2"
                  onClick={handleUnlimitedToggle}
                >
                  <span
                    className={twMerge(
                      'text-chariot-text-secondary',
                      classes.label
                    )}
                  >
                    {unlimitedLabel}
                  </span>
                  <Checkbox isChecked={isUnlimited} />
                </div>
              )}
            </div>
          )}
          {subtitle && (
            <p className="text-chariot-text-secondary mb-4 text-sm">
              {subtitle}
            </p>
          )}
          {!title && label && (
            <>
              {enableUnlimited ? (
                <div className="mb-2 flex items-center justify-between">
                  <label
                    className={twMerge(
                      'text-chariot-text-primary',
                      classes.label
                    )}
                  >
                    {label}
                  </label>
                  <div
                    className="flex cursor-pointer items-center gap-2"
                    onClick={handleUnlimitedToggle}
                  >
                    <span
                      className={twMerge(
                        'text-chariot-text-secondary',
                        classes.label
                      )}
                    >
                      {unlimitedLabel}
                    </span>
                    <Checkbox isChecked={isUnlimited} />
                  </div>
                </div>
              ) : (
                <label
                  className={twMerge(
                    'text-chariot-text-primary mb-2 block',
                    classes.label
                  )}
                >
                  {label}
                </label>
              )}
            </>
          )}
        </div>
      )}

      <div className="relative h-6 w-full">
        {/* Main track - normal range */}
        <div
          ref={trackRef}
          className={twMerge(
            'bg-chariot-stroke absolute top-1/2 -translate-y-1/2',
            classes.track,
            disabled || isUnlimited ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
          style={{
            width: '100%',
          }}
          onClick={handleTrackClick}
        />

        {/* Left stop indicator */}
        <div
          className={twMerge(
            'absolute top-1/2 h-3 w-0.5 -translate-y-1/2',
            disabled || isUnlimited
              ? 'bg-chariot-stroke'
              : 'bg-chariot-brand-primary'
          )}
          style={{ left: '0%' }}
        />

        {/* Right stop indicator */}
        <div
          className={twMerge(
            'absolute top-1/2 h-3 w-0.5 -translate-y-1/2',
            disabled || isUnlimited
              ? 'bg-chariot-stroke'
              : 'bg-chariot-brand-primary'
          )}
          style={{ left: '100%' }}
        />

        {/* Gradient effects on top of track - left side */}
        {!isUnlimited && (
          <div
            className={twMerge(
              'pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-full',
              classes.track,
              !isDragging && 'transition-all duration-100'
            )}
            style={{
              left: `${Math.max(0, thumbPosition - 10)}%`,
              width: `${Math.min(10, thumbPosition)}%`,
              background:
                disabled || isUnlimited
                  ? 'linear-gradient(to left, rgb(var(--chariot-stroke)) 32%, rgb(var(--chariot-stroke)) 62%, rgb(var(--chariot-stroke) / 0) 100%)'
                  : `linear-gradient(to left, rgb(var(--chariot-brand-primary)) 32%, rgb(var(--chariot-brand-primary)) 62%, rgb(var(--chariot-stroke) / 0) 100%)`,
              opacity: 0.8,
            }}
          />
        )}

        {/* Gradient effects on top of track - right side */}
        {!isUnlimited && (
          <div
            className={twMerge(
              'pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-full',
              classes.track,
              !isDragging && 'transition-all duration-100'
            )}
            style={{
              left: `${thumbPosition}%`,
              width: `${Math.min(10, 100 - thumbPosition)}%`,
              background:
                disabled || isUnlimited
                  ? 'linear-gradient(to right, rgb(var(--chariot-stroke)) 32%, rgb(var(--chariot-stroke)) 62%, rgb(var(--chariot-stroke) / 0) 100%)'
                  : `linear-gradient(to right, rgb(var(--chariot-brand-primary)) 32%, rgb(var(--chariot-brand-primary)) 62%, rgb(var(--chariot-stroke) / 0) 100%)`,
              opacity: 0.8,
            }}
          />
        )}

        {/* Dynamic thumb */}
        {!isUnlimited && (
          <div
            className={twMerge(
              'bg-chariot-brand-primary border-chariot-brand-primary absolute top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2',
              'outline-chariot-background-primary outline outline-4',
              'flex items-center justify-center text-xs font-bold text-white shadow-lg',
              classes.thumb,
              !isDragging && 'transition-all duration-100',
              isDragging && 'scale-110 cursor-grabbing',
              (disabled || isUnlimited) &&
                'bg-chariot-stroke border-chariot-stroke cursor-not-allowed'
            )}
            style={{
              left: `${thumbPosition}%`,
            }}
            onMouseDown={handleMouseDown}
          />
        )}

        {/* Min/Max markers under the track */}
        <div className="absolute top-full w-full">
          {!shouldHideMin && (
            <span
              className={twMerge(
                'absolute',
                classes.marker,
                disabled || isUnlimited
                  ? 'text-chariot-text-disabled'
                  : 'text-chariot-text-primary'
              )}
              style={{ left: '0%' }}
            >
              {min}
            </span>
          )}
          {!shouldMergeLabels && (
            <span
              className={twMerge(
                'absolute -translate-x-1/2',
                classes.marker,
                disabled || isUnlimited
                  ? 'text-chariot-text-disabled'
                  : 'text-chariot-text-primary'
              )}
              style={{ left: '100%' }}
            >
              {max}
            </span>
          )}
        </div>

        {/* Current value under handle */}
        {!isUnlimited && (
          <div
            className="absolute top-full -translate-x-1/2 text-center whitespace-nowrap"
            style={{
              left: `${thumbPosition}%`,
            }}
          >
            <span
              className={twMerge(
                'font-medium',
                classes.value,
                disabled
                  ? 'text-chariot-text-disabled'
                  : 'text-chariot-brand-primary'
              )}
            >
              {displayValue}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
